import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	AppService,
	AppSettings,
	AsamcoAuthService,
	IFrameParentService,
	MenuItem,
	MenuService,
	MessageBusService,
	MessageBusType,
	SignInCurrentUserResponse,
} from '@asamco/base';
import {
	isNullOrWhitespace,
	skipAuthInterceptor,
	skipDefaultResponseHandlerInterceptor,
	skipTenantInterceptor,
	toCssClass,
} from '@asamco/utils';
import { L10nTranslationService } from 'angular-l10n';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, exhaustMap, mapTo, shareReplay, switchMap, tap } from 'rxjs/operators';
import swal from 'sweetalert2';

import { Company, companyDefaults } from '../company/company.interface';
import { ConnectedApp } from '../connected-app/connected-app.interface';
import { Organisation, organisationDefaults } from '../organisation/organisation.interface';

export const sessionConnectedAppId = 'connected-app-id';
export const sessionConnectedAppApplicationType = 'connected-app-application-type';

@Injectable({
	providedIn: 'root',
})
export class TenantService {
	/** Keeps the last selected company */
	private companySwitch: BehaviorSubject<Company>;
	companySwitch$: Observable<Company>;

	/** Keeps the last selected organisation */
	private organisationSwitch: BehaviorSubject<Organisation>;
	organisationSwitch$: Observable<Organisation>;

	/** Keeps the last selected application */
	private switchApp: Subject<ConnectedApp> = new BehaviorSubject<ConnectedApp>(undefined);
	private currentApplication: ConnectedApp;

	application$: Observable<ConnectedApp> = this.switchApp.pipe(
		exhaustMap(app => {
			this.auth.applicationId = null;
			// We still need to store the current app
			if (app === undefined) {
				this.storeCurrentApp(app);

				this.restoreMainMenu();
				return of(app);
			} else {
				// sign-in
				return this.signInToApp(app);
			}
		}),
		tap(app => {
			this.updateBodyClass(app);

			this.msgBus.emit({
				payload: app,
				subType: 'switched',
				type: MessageBusType.ConnectedApp,
			});
		}),
		shareReplay(1)
	);

	// TODO: possible improvement
	private mainMenu$ = this.http
		.get<MenuItem[]>(`${this.app.deployUrl}assets/menu/menu.json`, {
			headers: skipTenantInterceptor(skipAuthInterceptor()),
		})
		.pipe(shareReplay(1));

	/**
	 * Constructor
	 */
	constructor(
		private http: HttpClient,
		private appService: AppService,
		private msgBus: MessageBusService,
		private ts: L10nTranslationService,
		private auth: AsamcoAuthService,
		private app: AppSettings,
		private menu: MenuService,
		private iframeParentService: IFrameParentService
	) {
		// TODO: We cannot call leave application from the logoff modal :(... must be a better way to do this
		this.msgBus
			.on(MessageBusType.LeaveConnectedApp)
			.pipe(tap(_ => this.leaveApplication()))
			.subscribe({error: console.error});

		// this.switchApplication(undefined);
		this.companySwitch = new BehaviorSubject<Company>(companyDefaults());
		this.companySwitch$ = this.companySwitch.asObservable();

		this.organisationSwitch = new BehaviorSubject<Organisation>(organisationDefaults());
		this.organisationSwitch$ = this.organisationSwitch.asObservable();

		// Update the company when a sign in occurs. This is necessary to
		// keep the company name visible in the menu after the page is refreshed.
		this.iframeParentService.signIn$.subscribe((signIn: SignInCurrentUserResponse) => {
			if (signIn?.app?.company) {
				this.switchCompany(signIn.app.company as Company);
			}
		});
	}

	private signInToApp(_: ConnectedApp): Observable<ConnectedApp> {
		return of(_).pipe(
			// Show sign in loading message
			tap(app => this.appService.beginLoading({ message: `Signing in to ${app.name}, please wait...`, showDelay: 0 })),
			// Setup up connected app
			switchMap(app => {
				return this.http
					.get(`connectedApp/sign-in/${app.oid}`, { headers: skipDefaultResponseHandlerInterceptor(skipTenantInterceptor()) })
					.pipe(
						tap(data => {
							(<any>window).epoReturnData = data;
						}),
						mapTo(app),
						tap(connectedApp => {
							this.auth.applicationId = connectedApp.oid;
						}),
						catchError(() => {
							this.appService.endLoading(false);
							swal.fire({
								title: this.ts.translate('SWAL_TITLE.ERROR'),
								text: 'An error occured while signing in to the application',
								icon: 'error',
							});
							this.leaveApplication();
							return of(null);
						})
					);
			}),
			tap(app => {
				this.storeCurrentApp(app);
				this.appService.endLoading();
			})
		);
	}

	private restoreMainMenu() {
		this.mainMenu$.subscribe(menu => {
			this.menu.setMenu(menu);
		});
	}

	/** Return the company observable */
	getCompanySwitch(): Observable<Company> {
		return this.companySwitch$;
	}

	/** Return the organisation observable */
	getOrganisationSwitch(): Observable<Organisation> {
		return this.organisationSwitch$;
	}

	/** Switch company to new company id */
	switchCompany(company: Company) {
		this.companySwitch.next(company);
	}

	/** Switch organisation to new organisation id */
	switchOrganisation(organisationId: Organisation) {
		this.organisationSwitch.next(organisationId);
	}

	updateBodyClass(app: ConnectedApp) {
		const classPrefix = 'connected-app-';
		// First remove the old class(es)
		document.body.classList.forEach(function (className) {
			if (className.startsWith(classPrefix)) {
				document.body.classList.remove(className);
			}
		});

		// Add the new class
		const appName = app && app.connectedAppType && app.connectedAppType.name && app.connectedAppType.name.toLowerCase();
		if (!isNullOrWhitespace(appName)) {
			document.body.classList.add(toCssClass(`${classPrefix}${appName}`));
		}
	}

	/**
	 * Switch application
	 */
	switchApplication(application: ConnectedApp) {
		this.switchApp.next(application);
	}

	/**
	 * Gets the current application
	 */
	getCurrentApplication(): ConnectedApp {
		return this.currentApplication;
	}

	/**
	 * Gets the current application
	 */
	getCurrentCompany(): Company {
		return this.companySwitch.getValue();
	}

	/**
	 * Store the last used application in session
	 */
	storeCurrentApp(app: ConnectedApp): void {
		if (app) {
			sessionStorage.setItem(sessionConnectedAppId, app.oid);
			this.currentApplication = app;
			// sessionStorage.setItem(sessionConnectedAppApplicationType, app.type.applicationType);
		}
	}

	/**
	 * Add's applicationId query param to the (odata) request
	 * @param request The request devextreme is going to execute
	 */
	dxBeforeSend(request: { params: Object }) {
		const currentApplication = this.getCurrentApplication();
		if (currentApplication !== undefined) {
			request.params['application-id'] = currentApplication.oid;
		}
	}

	/**
	 * Exit application
	 */
	leaveApplication(): void {
		if (this.currentApplication != null) {
			this.http
				.get(`connectedApp/sign-out/${this.currentApplication.oid}`, {
					headers: skipDefaultResponseHandlerInterceptor(skipTenantInterceptor()),
				})
				.subscribe({error: console.error});
		}
		sessionStorage.removeItem(sessionConnectedAppId);
		this.currentApplication = undefined;
		this.switchApp.next(undefined);
	}
}
